/**
 * Reverse geocoding utility with caching.
 * Uses Smarty via the SafePath backend proxy (/smarty/reverse-geocode).
 */

import { reverseGeocode as smartyReverseGeocode } from '../services/smarty.service';

const geocodeCache = new Map();

function getCacheKey(lat, lon) {
  return `${Number(lat).toFixed(5)},${Number(lon).toFixed(5)}`;
}

function formatAddress(result) {
  if (!result?.address) return null;
  const addr = result.address;
  const parts = [];

  if (addr.street) parts.push(addr.street);
  const city = addr.city;
  if (city) parts.push(city);
  if (addr.state_abbreviation) parts.push(addr.state_abbreviation);

  return parts.length > 0 ? parts.join(', ') : null;
}

/**
 * Reverse geocode a lat/lon pair. Returns a promise that resolves to an address string.
 * Results are cached so the same coordinates are only looked up once.
 */
export async function reverseGeocode(lat, lon) {
  if (lat == null || lon == null) return null;

  const key = getCacheKey(lat, lon);
  if (geocodeCache.has(key)) return geocodeCache.get(key);

  try {
    const data = await smartyReverseGeocode(lat, lon);
    const firstResult = data?.results?.[0];
    const address = formatAddress(firstResult);
    geocodeCache.set(key, address);
    return address;
  } catch (err) {
    console.error('Reverse geocode failed:', err);
    return null;
  }
}

/**
 * Get a cached address synchronously. Returns null if not yet resolved.
 */
export function getCachedAddress(lat, lon) {
  if (lat == null || lon == null) return null;
  return geocodeCache.get(getCacheKey(lat, lon)) || null;
}
